import _ from 'lodash'
import React, {useState, useEffect, forwardRef, useCallback, useRef, useMemo} from 'react';
import { FieldTypes, generateFormFields } from 'utils/formUtils';
import ProductAPI from 'restAPI/ProductAPI'
import SubProductAPI from 'restAPI/SubProductAPI';
import { Form ,Alert } from 'antd'
import SubProductEnvironmentTypeAPI from 'restAPI/SubProductEnvironmentTypeAPI';
import Vocabulary from 'components/Vocabulary';
import FindingAPI from 'restAPI/FindingAPI';
import BaseModal from 'components/BaseModal';
import message from 'components/CustomMessage';
import BusinessUnitAPI from 'restAPI/BusinessUnitAPI';
import { useActiveUserContext } from 'contexts/ActiveUserContext';
import EnvironmentName from 'views/GlobalSettings/EnvironmentName';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';


export default forwardRef(function FindingsBasicForm({finding, afterClose}, ref) {
    const [products, setProducts] = useState();
    const [businessUnit, setBusinessUnit] = useState(null)
    const [subProducts, setSubProducts] = useState(null);
    const [environments, setEnvironments] = useState(null);
    const [form] = Form.useForm()
    const [formData, setFormData] = useState({});
    const [succeeded, setSucceeded] = useState(false);
    const [alertVisible, setAlertVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currentTicketSystem, setCurrentTicketSystem] = useState();
    const [newTicktSystem, setNewTicketSystem] = useState();
    const featureFlag = useFeatureFlag();

    const isBUDisabled = finding.source?.name !== "HackerOne" || !featureFlag.HACKERONEONE_BU_CHANGE;
    const hasBUChanged = useRef(false);
    const activeUser = useActiveUserContext();

    useEffect(() => {
        const { promise, cancel } = BusinessUnitAPI.getAll();
        promise.then((data) => setBusinessUnit(data));
        return cancel;
    },[form, isBUDisabled])

    useEffect(() => {
        const { promise, cancel } = ProductAPI.getByBU(activeUser.businessUnitId);
        promise.then(setProducts, console.error);

        const {promise: promise2, cancel: cancel2} = SubProductAPI.getByBU(activeUser.businessUnitId, finding.product?.id);
        promise2.then(setSubProducts, console.error);

        const {promise: promise3, cancel: cancel3} = SubProductEnvironmentTypeAPI.getByBU(activeUser.businessUnitId, finding.product.id, finding.appId);
        promise3.then(response => {
            setEnvironments(response)
        })
        return () => {cancel();cancel2();cancel3();};
    },[finding, activeUser.businessUnitId])

    useEffect(() => {
        if(formData.businessUnitId && form){
            form.setFieldsValue({
                product: undefined,
                subProduct: undefined,
                environmentId: undefined
            });
            setFormData(formData => ({ ...formData, product: undefined }));
            setProducts(null)
            setSubProducts([])
            setEnvironments([])
            const {promise, cancel} = ProductAPI.getByBU(formData.businessUnitId || activeUser.businessUnitId)
            promise.then(setProducts, console.error);
            return cancel;
        }
    },[formData.businessUnitId, form, activeUser.businessUnitId]);

    useEffect(() => {
        if(formData.product && form){
            form.setFieldsValue({
                subProduct: undefined,
                environmentId: undefined
            });
            setSubProducts(null);
            const {promise, cancel} = SubProductAPI.getByBU(formData.businessUnitId || activeUser.businessUnitId, formData.product);
            promise.then(setSubProducts, console.error);
            return cancel;
        }
    }, [formData.product, form, formData.businessUnitId, activeUser.businessUnitId]);

    useEffect(() => {
        const { promise, cancel } = FindingAPI.checkforTicketConfig(activeUser.businessUnitId, finding.product.id, finding.subProduct.id);
        promise.then((res) => {
            setCurrentTicketSystem(res.ticketSystemType);
        });
        return cancel;
    }, [finding, activeUser.businessUnitId]);

    useEffect(() => {
        const updateSubproduct = async () => {
            if(formData.subProduct && form){
                form.setFieldsValue({
                    environmentId: undefined
                });
                setEnvironments(null);
                setLoading(true);
                await SubProductEnvironmentTypeAPI.getAllQuick(formData.subProduct).promise.then(response => {
                    setEnvironments(response)
                    const productionId = response.find(env => env.name === "Production")?.id
                    form.setFieldsValue({ environmentId:  productionId })
                });
                const values = form.getFieldsValue();
                const [ buId, apId, aspId ] = [values.businessUnitId, values.product, values.subProduct];
                if (buId && apId && aspId) {
                    const ticketConfig =  await FindingAPI.checkforTicketConfig(buId, apId, aspId).promise;
                    setLoading(false);
                    if(!ticketConfig){
                       setAlertVisible(true);
                    } else {
                        setNewTicketSystem(ticketConfig.ticketSystemType)
                    }
                }
            }
        }
        updateSubproduct()
    }, [formData.subProduct, form])

    const onSuccess = useCallback(async () => {
        await form.validateFields();
        const values = form.getFieldsValue();
        const [buId,apId,aspId,aeId, note] = [values.businessUnitId,values.product,values.subProduct,values.environmentId, values.note];

        // If nothing has changed, return
        if (activeUser.businessUnitId === buId && finding.product?.id === apId && finding.appId === aspId && finding.envId === aeId) {
            return;
        }

        hasBUChanged.current = activeUser.businessUnitId !== buId;

        let MoveFindingAPI;
        
       if(isBUDisabled){
        MoveFindingAPI = FindingAPI.moveFindingV2(finding.id, aeId, {note})
       }else{
        MoveFindingAPI = FindingAPI.moveFinding({ 
            buId,
            apId,
            aspId,
            aeId,
            note,
            findings: [finding.id]
        }, finding.id)
       }

       try{
        await MoveFindingAPI.promise
        setSucceeded(true);
        message.success("Successfully updated Basic Information.");
        } catch (e) {
            throw e;
        }
        
    },[finding,form, activeUser.businessUnitId, isBUDisabled])

    const environmentOptions = useMemo(() => {
        if (!environments) {
          return [];
        }
        return environments.map((environment) => ({
          value: environment.id,
          label: <EnvironmentName name={environment} />,
        }));
      },[environments])

    const fields = [
        {
            name: "businessUnitId",
            label: "Organization",
            type: FieldTypes.SELECT,
            options: businessUnit,
            loading: businessUnit===null,
            colSpan: 24,
            disabled: isBUDisabled,
            props: {
                autoFocus: !isBUDisabled
            }
    },{
            name: "product",
            label: <Vocabulary name="product" />,
            type: FieldTypes.SELECT,
            options: products,
            loading: _.isNull(products),
            notFoundContent: <>No <Vocabulary name="product" isLowerCase={true} /> found</> ,
            rules: [{
                required: true,
                message: <>Select a <Vocabulary name="product"  isLowerCase={true} /> name.</>
            }],
            props: {
                autoFocus: isBUDisabled
            }
        },
        {
            name: "subProduct",
            label: <Vocabulary name="subProduct" />,
            type: FieldTypes.SELECT,
            options: subProducts,
            loading: _.isNull(subProducts),
            notFoundContent:  <>No <Vocabulary name="subProduct" isLowerCase={true} /> found</>,
            rules: [{
                required: true,
                message: <>Select a <Vocabulary name="subProduct" isLowerCase={true} /> name.</>
            }]
        },
        {
            label: "Environment",
            name: "environmentId",
            type: FieldTypes.SELECT,
            options: environmentOptions,
            loading: _.isNull(environments),
            notFoundContent: 'No environments found',
            rules: [{
                required: true,
                message: <>Select an environment</>
            }]

        },
        {
            name: "note",
            label: "Note",
            type: FieldTypes.TEXT_AREA,
            required: true,
            customLengthRule: {
                max: 1000,
                message: "Maximum length is 1000 characters"
            },
        }
    ];
    
    const modalProps = {
        title: "Edit Basic Information",
        onOk: onSuccess
    }
    return(
        <BaseModal
        ref={ref}
        modalProps={{...modalProps}}
        afterClose={() => afterClose(succeeded, hasBUChanged.current)}
        loading={loading}
        >
            {alertVisible && <Alert className='m-b-xs' banner message={<>Ticket Configuration doesn't exist for the selected <Vocabulary name="product" /> and <Vocabulary name="subProduct" />, tickets attached to the finding won't be migrated.</>}/>}
            {(!_.isEmpty(finding.armorcodeTicket) && !_.isUndefined(newTicktSystem) && currentTicketSystem !== newTicktSystem) && <Alert className='m-b-xs' banner message={<>Ticket Configurations do not match [{capitalize(currentTicketSystem)} to {capitalize(newTicktSystem)}] for the selected <Vocabulary name="product" /> and <Vocabulary name="subProduct" />, tickets attached to the finding won't be migrated.</>}/>}
            <Form
                form={form}
                onValuesChange={(data) => {
                    setFormData({...formData, ...data});
                    if(alertVisible) setAlertVisible(false);
                  }
                }
                layout="vertical"
                initialValues={{
                    businessUnitId: activeUser.businessUnitId,
                    product: finding.product?.id,
                    subProduct: finding.appId,
                    environmentId: finding.envId
                }}
            >
                {generateFormFields(fields)}
            </Form>
        </BaseModal>
    )
})

const capitalize = (text) => _.capitalize(_.lowerCase(text));
